/**
 * GroupByAddPopover — column-chooser popover for the GROUP BY
 * `[+ add group-by]` affordance. Mirrors `OrderByAddPopover` /
 * `FromAddTable` / `SelectListAddColumn`: only the `*Enabled` sub-component
 * subscribes to TanStack Query so VQE-RO unit tests don't need a
 * `QueryClientProvider`.
 *
 * Selecting a suggestion appends a new GROUP BY entry. The added text is the
 * `formatColumnRef`-formatted form of the picked column (alias-qualified when
 * the FROM clause has a matching alias, schema-qualified otherwise).
 *
 * PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — add/remove GROUP BY.
 */

import { useAnalytics } from '../../../../../core/analytics/useAnalytics';
import type { VqeEditContext } from '../../edit/editContext';
import { toCommitCtx } from '../../edit/editContext';
import { addGroupBy } from '../../edit/edits/addGroupBy';
import { removeGroupBy } from '../../edit/edits/removeGroupBy';
import { formatColumnRef } from '../../edit/edits/selectItemHelpers';
import { runSequentialStatementEdits } from '../../edit/runSequentialStatementEdits';
import { vqeCopy } from '../../ux/vqeCopy';
import { CommandPickerPopover } from '../common/CommandPickerPopover';
import { useInScopeColumnPicker } from '../common/useInScopeColumnPicker';

export interface GroupByAddPopoverProps {
  readonly editContext?: VqeEditContext;
  /** Trigger text; defaults to the clause card's own. The clause add row passes a short one. */
  readonly triggerLabel?: string;
}

export function GroupByAddPopover({ editContext, triggerLabel = vqeCopy.actions.addGroupBy }: GroupByAddPopoverProps) {
  if (!editContext) {
    return (
      <CommandPickerPopover triggerLabel={triggerLabel} testId="vqe-group-by-add" />
    );
  }
  return <GroupByAddPopoverEnabled editContext={editContext} triggerLabel={triggerLabel} />;
}

interface EnabledProps {
  readonly editContext: VqeEditContext;
  readonly triggerLabel: string;
}

function GroupByAddPopoverEnabled({ editContext, triggerLabel }: EnabledProps) {
  const { track } = useAnalytics();
  const picker = useInScopeColumnPicker(
    editContext,
    `${editContext.documentId}::${editContext.statementId}::groupby`,
    { selection: 'group-by' },
  );

  const addOne = (key: string) => {
    // A checked row is already grouped on — picking it removes it.
    const selectedIndex = picker.selectedIndexOf(key);
    if (selectedIndex !== undefined) {
      void removeGroupBy(selectedIndex, toCommitCtx(editContext));
      track('feature:vqe_picker_uncheck', { target: 'group_by' });
      return;
    }
    const col = picker.getColumn(key);
    if (!col) return;
    void addGroupBy(formatColumnRef(col, picker.refs), toCommitCtx(editContext));
  };

  const addAll = (relationFolded: string) => {
    const cols = picker.getUnselectedColumnsForRelation(relationFolded);
    if (cols.length === 0) return;
    void runSequentialStatementEdits(editContext, cols, (c, ctx) =>
      addGroupBy(formatColumnRef(c, picker.refs), ctx),
    );
    track('feature:vqe_add_all_columns', { count: cols.length, target: 'group_by' });
  };

  return (
    <CommandPickerPopover
      triggerLabel={triggerLabel}
      testId="vqe-group-by-add"
      disabled={!picker.enabled}
      tooltip={picker.enabled ? 'Add a column to GROUP BY' : undefined}
      searchPlaceholder="Search columns…"
      groups={picker.groups}
      onSelect={picker.enabled ? addOne : undefined}
      closeOnSelect
      onGroupAction={picker.enabled ? addAll : undefined}
      scopeToggle={picker.scopeToggle}
      loading={picker.isLoading}
      notice={picker.notice}
      emptyMessage={
        picker.emptyMessage ??
        (picker.hasSnapshot
          ? 'No columns in scope. Add a FROM table or refresh the schema.'
          : 'No schema cache yet — connect to a database to populate suggestions.')
      }
    />
  );
}
